/**
 * Mikrofondan qisqa yozuv → 16 kHz mono WAV (base64). Telegram WebView'larida (iOS WKWebView, Android WebView)
 * Web Speech API yo'q, lekin getUserMedia + Web Audio bor — shuning uchun ovozni o'zimiz yozib, serverga (Gemini) yuboramiz.
 * WAV — hamma joyda bir xil (MediaRecorder iOS'da mp4, Android'da webm beradi).
 */

const RATE = 16000;

export interface Recording { b64: string; peak: number; ms: number }
export interface Recorder { stop: () => Promise<Recording>; cancel: () => void }

export const canRecord = () => {
  try { return !!navigator.mediaDevices?.getUserMedia && !!(window.AudioContext || (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext); } catch { return false; }
};

/*
 * Mikrofon oqimi dars davomida BIR MARTA ochiladi: Telegram Mini App ruxsatini eslab qolmaydi va har bir getUserMedia'da
 * «Разрешить … доступ к микрофону?» oynasini qayta ko'rsatadi. Yozuvlar orasida trek o'chiq (enabled=false) — faqat
 * jimlik keladi, hech narsa yig'ilmaydi va yuborilmaydi. Dars tugaganda / ilova yashirilganda — releaseMic().
 */
let held: MediaStream | null = null;
let pending: Promise<MediaStream> | null = null;

const live = (s: MediaStream | null) => !!s && s.getAudioTracks().some((t) => t.readyState === 'live');
const mute = (s: MediaStream, on: boolean) => s.getAudioTracks().forEach((t) => { t.enabled = on; });

/** Mikrofon allaqachon ochiqmi (ruxsat shu darsda berilgan). */
export const micHeld = () => live(held);

async function acquire(): Promise<MediaStream> {
  if (live(held)) return held!;
  pending ??= navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
    .then((s) => { held = s; mute(s, false); return s; })
    .finally(() => { pending = null; });
  return pending;
}

/** Mikrofonni butunlay yopish (dars tugadi, chiqildi yoki ilova yashirildi). */
export function releaseMic() {
  held?.getTracks().forEach((t) => t.stop());
  held = null;
}
try { document.addEventListener('visibilitychange', () => { if (document.hidden) releaseMic(); }); } catch { /* ignore */ }

/** Yozishni boshlaydi (mikrofon shu darsda hali ochilmagan bo'lsa — ochadi). Ruxsat berilmasa — NotAllowedError. */
export async function startRecording(): Promise<Recorder> {
  // AudioContext bosish (user gesture) ichida, getUserMedia'dan OLDIN yaratiladi — iOS aks holda uni «suspended» qoldiradi
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AC();
  const resumed = ctx.resume().catch(() => {});
  let stream: MediaStream;
  try {
    stream = await acquire();
  } catch (e) { ctx.close().catch(() => {}); throw e; }
  mute(stream, true);
  // resume() ba'zi WebView'larda hech qachon tugamaydi — yozuv osilib qolmasin
  await Promise.race([resumed, new Promise((r) => setTimeout(r, 800))]);
  const src = ctx.createMediaStreamSource(stream);
  // ScriptProcessor eskirgan, lekin AudioWorklet'dan farqli hamma WebView'da bor; 2–4 s yozuv uchun yetarli
  const proc = ctx.createScriptProcessor(4096, 1, 1);
  const chunks: Float32Array[] = [];
  const t0 = performance.now();
  proc.onaudioprocess = (e) => { chunks.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
  src.connect(proc); proc.connect(ctx.destination);
  let closed = false;
  const close = () => {
    if (closed) return; closed = true;
    try { proc.disconnect(); src.disconnect(); } catch { /* ignore */ }
    mute(stream, false); // oqim ochiq qoladi (keyingi so'zda ruxsat so'ralmaydi), lekin ovoz kelmaydi
    ctx.close().catch(() => {});
  };
  return {
    cancel: close,
    stop: async () => {
      const rate = ctx.sampleRate; close();
      const pcm = downsample(concat(chunks), rate, RATE);
      let peak = 0; for (const v of pcm) { const a = Math.abs(v); if (a > peak) peak = a; }
      return { b64: toB64(wav(pcm, RATE)), peak, ms: performance.now() - t0 };
    },
  };
}

function concat(cs: Float32Array[]) {
  const out = new Float32Array(cs.reduce((n, c) => n + c.length, 0));
  let o = 0; for (const c of cs) { out.set(c, o); o += c.length; }
  return out;
}

/** O'rtacha qiymat bilan kamaytirish (oddiy anti-aliasing). */
function downsample(x: Float32Array, from: number, to: number) {
  if (from <= to) return x;
  const r = from / to, n = Math.floor(x.length / r), out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const a = Math.floor(i * r), b = Math.min(x.length, Math.floor((i + 1) * r));
    let s = 0; for (let j = a; j < b; j++) s += x[j]!;
    out[i] = s / Math.max(1, b - a);
  }
  return out;
}

function wav(pcm: Float32Array, rate: number) {
  const buf = new ArrayBuffer(44 + pcm.length * 2), v = new DataView(buf);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + pcm.length * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, 'data'); v.setUint32(40, pcm.length * 2, true);
  for (let i = 0; i < pcm.length; i++) { const s = Math.max(-1, Math.min(1, pcm[i]!)); v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true); }
  return new Uint8Array(buf);
}

function toB64(b: Uint8Array) {
  let s = ''; const step = 0x8000;
  for (let i = 0; i < b.length; i += step) s += String.fromCharCode(...b.subarray(i, i + step));
  return btoa(s);
}
